import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Save, X, ArrowUp, ArrowDown, Plus, Loader2, MapPin, Star, ArrowDownUp, Link2 } from 'lucide-react';
import { adminApi } from '../lib/adminApi';
import { SORT_OPTIONS } from '../lib/constants';
import { invalidateSettingsCache } from '../lib/useSettings';

const DEFAULT_CITIES = ['Delhi', 'Gurugram', 'Noida', 'Delhi NCR'];

export default function AdminHomeSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [allArtists, setAllArtists] = useState([]);
  const [featuredSlugs, setFeaturedSlugs] = useState([]);
  const [cities, setCities] = useState([]);
  const [newCity, setNewCity] = useState('');
  const [defaultSort, setDefaultSort] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');

  useEffect(() => {
    Promise.all([adminApi.list(), adminApi.listSettings()])
      .then(([arts, settings]) => {
        setAllArtists(arts || []);
        setFeaturedSlugs(Array.isArray(settings?.featured_artist_slugs) ? settings.featured_artist_slugs : []);
        setCities(Array.isArray(settings?.cities) && settings.cities.length ? settings.cities : DEFAULT_CITIES);
        setDefaultSort(settings?.default_artist_sort || '');
        setRegistrationUrl(settings?.artist_registration_url || '');
      })
      .catch(() => toast.error('Could not load settings'))
      .finally(() => setLoading(false));
  }, []);

  const artistBySlug = useMemo(() => Object.fromEntries(allArtists.map(a => [a.slug, a])), [allArtists]);
  const availableArtists = useMemo(
    () => allArtists.filter(a => a.published !== false && !featuredSlugs.includes(a.slug)),
    [allArtists, featuredSlugs]
  );

  const saveFeatured = async (newOrder) => {
    setSaving('featured');
    try {
      await adminApi.setSetting('featured_artist_slugs', newOrder);
      setFeaturedSlugs(newOrder);
      toast.success('Featured artists updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(''); }
  };

  const addToFeatured = (slug) => {
    if (featuredSlugs.includes(slug)) return;
    if (featuredSlugs.length >= 6) { toast.error('Maximum 6 featured artists. Remove one first.'); return; }
    saveFeatured([...featuredSlugs, slug]);
  };
  const removeFromFeatured = (slug) => saveFeatured(featuredSlugs.filter(s => s !== slug));
  const moveFeatured = (idx, dir) => {
    const next = [...featuredSlugs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    saveFeatured(next);
  };

  const saveCities = async (list) => {
    setSaving('cities');
    try {
      await adminApi.setSetting('cities', list);
      setCities(list);
      toast.success('Cities updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(''); }
  };
  const addCity = () => {
    const v = newCity.trim();
    if (!v) return;
    if (cities.map(c => c.toLowerCase()).includes(v.toLowerCase())) { toast.error('Already in list'); return; }
    saveCities([...cities, v]);
    setNewCity('');
  };
  const removeCity = (name) => saveCities(cities.filter(c => c !== name));

  const saveDefaultSort = async (val) => {
    setSaving('sort');
    try {
      await adminApi.setSetting('default_artist_sort', val);
      setDefaultSort(val);
      invalidateSettingsCache();
      toast.success(val ? `Default sort: ${val}` : 'Default sort cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(''); }
  };

  const saveRegistrationUrl = async () => {
    const v = registrationUrl.trim();
    if (v && !/^https?:\/\//i.test(v)) { toast.error('Enter a full URL starting with http:// or https://'); return; }
    setSaving('regurl');
    try {
      await adminApi.setSetting('artist_registration_url', v);
      setRegistrationUrl(v);
      invalidateSettingsCache();
      toast.success(v ? 'Registration link saved' : 'Registration link cleared (using default)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(''); }
  };

  if (loading) return <div className="text-sm uppercase tracking-[0.25em] text-neutral-500">Loading…</div>;

  return (
    <div data-testid="admin-home-settings-page" className="max-w-5xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Manage</div>
        <h1 className="mt-1 font-serif text-3xl md:text-4xl">Home & Browse Settings</h1>
        <p className="mt-3 text-sm text-neutral-600 max-w-2xl leading-relaxed">
          Choose which 6 artists are featured on the home page, manage cities shown in the Browse filter, and set the default sort order for the Artists directory.
        </p>
      </div>

      {/* ----- Featured Artists ----- */}
      <section className="bg-white border border-[#eaeaea] p-5 md:p-6 mb-8" data-testid="featured-artists-section">
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-4 h-4 text-[#eb5381]" />
          <h2 className="font-serif text-2xl">Featured Artists on Home Page</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          Pick up to 6 — appears in the "Featured artists" section in the order shown. If empty, the home page falls back to any artist flagged "Featured" in the artist editor.
        </p>

        {featuredSlugs.length === 0 ? (
          <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-6 text-center">
            None selected yet. Pick artists from the list below.
          </div>
        ) : (
          <ol className="space-y-2 mb-6">
            {featuredSlugs.map((slug, idx) => {
              const a = artistBySlug[slug];
              return (
                <li key={slug} className="flex items-center gap-3 border border-[#eaeaea] p-2.5" data-testid={`featured-row-${slug}`}>
                  <div className="w-6 text-center text-[10px] uppercase tracking-[0.25em] text-neutral-500">{idx + 1}</div>
                  {a?.profile_image && <img src={a.profile_image} alt="" className="w-12 h-12 object-cover" />}
                  <div className="flex-1 min-w-0">
                    {a ? (
                      <>
                        <div className="font-serif text-lg leading-tight truncate">{a.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">{a.city}{a.published === false ? ' · UNPUBLISHED' : ''}</div>
                      </>
                    ) : (
                      <div className="text-sm text-red-500">{slug} (not found — was the artist deleted?)</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveFeatured(idx, -1)} disabled={idx === 0 || saving === 'featured'} className="w-8 h-8 grid place-items-center border border-neutral-300 hover:border-[#eb5381] disabled:opacity-40" aria-label="Move up" data-testid={`featured-up-${slug}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveFeatured(idx, 1)} disabled={idx === featuredSlugs.length - 1 || saving === 'featured'} className="w-8 h-8 grid place-items-center border border-neutral-300 hover:border-[#eb5381] disabled:opacity-40" aria-label="Move down" data-testid={`featured-down-${slug}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeFromFeatured(slug)} disabled={saving === 'featured'} className="w-8 h-8 grid place-items-center border border-neutral-300 hover:border-red-500 hover:text-red-500" aria-label="Remove" data-testid={`featured-remove-${slug}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="border-t border-[#eaeaea] pt-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Add Artists ({featuredSlugs.length}/6 selected)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
            {availableArtists.map(a => (
              <button
                key={a.slug}
                onClick={() => addToFeatured(a.slug)}
                disabled={featuredSlugs.length >= 6 || saving === 'featured'}
                data-testid={`featured-add-${a.slug}`}
                className="flex items-center gap-3 border border-[#eaeaea] hover:border-[#eb5381] p-2 text-left disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {a.profile_image && <img src={a.profile_image} alt="" className="w-10 h-10 object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-sm leading-tight truncate">{a.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 truncate">{a.city}</div>
                </div>
                <Plus className="w-4 h-4 shrink-0 text-[#eb5381]" />
              </button>
            ))}
            {availableArtists.length === 0 && (
              <div className="col-span-full text-xs text-neutral-500 italic text-center py-6">All published artists are already featured (or none exist). Add more artists first.</div>
            )}
          </div>
        </div>
      </section>

      {/* ----- Cities ----- */}
      <section className="bg-white border border-[#eaeaea] p-5 md:p-6 mb-8" data-testid="cities-section">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4 text-[#eb5381]" />
          <h2 className="font-serif text-2xl">Cities (Browse Filter)</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          These appear as checkboxes in the Artist City filter on <a href="/artists" target="_blank" rel="noreferrer" className="text-[#eb5381] underline">/artists</a>. Add cities as you expand to new locations.
        </p>

        <ul className="flex flex-wrap gap-2 mb-5">
          {cities.map(c => (
            <li key={c} className="inline-flex items-center gap-2 border border-[#eaeaea] pl-3 pr-1 py-1 text-sm" data-testid={`city-chip-${c.toLowerCase().replace(/\s+/g, '-')}`}>
              <span>{c}</span>
              <button onClick={() => removeCity(c)} disabled={saving === 'cities'} className="w-6 h-6 grid place-items-center hover:bg-red-500 hover:text-white transition-colors" aria-label={`Remove ${c}`} data-testid={`city-remove-${c.toLowerCase().replace(/\s+/g, '-')}`}>
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 max-w-md">
          <input
            type="text"
            value={newCity}
            onChange={(e) => setNewCity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCity()}
            placeholder="Add a city (e.g. Mumbai)"
            data-testid="add-city-input"
            className="flex-1 bg-white border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2.5 text-sm"
          />
          <button
            type="button"
            onClick={addCity}
            disabled={!newCity.trim() || saving === 'cities'}
            data-testid="add-city-btn"
            className="inline-flex items-center gap-2 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-50 text-white px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors"
          >
            {saving === 'cities' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>
      </section>

      {/* ----- Default Sort ----- */}
      <section className="bg-white border border-[#eaeaea] p-5 md:p-6" data-testid="default-sort-section">
        <div className="flex items-center gap-2 mb-1">
          <ArrowDownUp className="w-4 h-4 text-[#eb5381]" />
          <h2 className="font-serif text-2xl">Default Sort on Browse Artists Page</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          The sort applied when a visitor lands on <a href="/artists" target="_blank" rel="noreferrer" className="text-[#eb5381] underline">/artists</a> without picking a sort. Visitors can still override from the page.
        </p>

        <div className="flex items-center gap-3 max-w-md">
          <select
            value={defaultSort}
            onChange={(e) => saveDefaultSort(e.target.value)}
            disabled={saving === 'sort'}
            data-testid="default-sort-select"
            className="flex-1 bg-white border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2.5 text-sm"
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {saving === 'sort' && <Loader2 className="w-4 h-4 animate-spin text-[#eb5381]" />}
        </div>
      </section>

      {/* ----- Register as MUA Link ----- */}
      <section className="bg-white border border-[#eaeaea] p-5 md:p-6 mt-8" data-testid="registration-url-section">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-[#eb5381]" />
          <h2 className="font-serif text-2xl">"Register as MUA" Link</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          The destination for every "Register as MUA / Register as Artist" button across the site (navbar, home page, and the For Artists page). Paste a Google Form link or any URL. Leave blank to use the default form.
        </p>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl">
          <input
            type="url"
            value={registrationUrl}
            onChange={(e) => setRegistrationUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveRegistrationUrl()}
            placeholder="https://docs.google.com/forms/..."
            data-testid="registration-url-input"
            className="flex-1 bg-white border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2.5 text-sm"
          />
          <button
            type="button"
            onClick={saveRegistrationUrl}
            disabled={saving === 'regurl'}
            data-testid="registration-url-save-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-50 text-white px-6 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors"
          >
            {saving === 'regurl' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
        {registrationUrl.trim() && /^https?:\/\//i.test(registrationUrl.trim()) && (
          <a href={registrationUrl.trim()} target="_blank" rel="noreferrer" data-testid="registration-url-preview" className="inline-block mt-3 text-xs text-[#eb5381] underline break-all">
            Test link ↗
          </a>
        )}
      </section>
    </div>
  );
}
